import { useDroppable } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import { FC, Fragment, KeyboardEvent, MouseEvent } from 'react';
import { VEHICLE_CARD_HEIGHT } from 'src/components/dispatch/vehicleCardStyles';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { DispatchCardView } from './DispatchCardView';
import { DisplayMetric } from './DisplayMetricToggle';

type GroupPosition = 'single' | 'start' | 'middle' | 'end';

interface VehicleRowViewProps {
  vehicle: Vehicle;
  rowId: string;
  cards: DispatchCard[];
  enabled?: boolean;
  selectedCardIds?: Set<string>;
  duplicateStoreNames?: Set<string>;
  routeDeviationCardIds?: Set<string>;
  geoAlerts?: Map<string, GeoAlertInfo>;
  displayMetrics?: Set<DisplayMetric>;
  groupPosition?: GroupPosition;
  /** Sticky left にあるときのみ true。中央ボードでは false */
  showVehicleInfo?: boolean;
  rowSelected?: boolean;
  /** 他 row / 未割当からのドロップ予定 index。null なら未表示。
   *  index === cards.length の場合は末尾に縦バーを描画。 */
  dropPreviewIndex?: number | null;
  /** 直近のドロップで動いたカード id。一定時間パルスアニメで強調表示する。 */
  recentlyMovedCardIds?: Set<string>;
  onSelectCard?: (
    card: DispatchCard,
    vehicle: Vehicle,
    orderIndex: number,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
}

export const VehicleRowView: FC<VehicleRowViewProps> = ({
  vehicle,
  rowId,
  cards,
  enabled = true,
  selectedCardIds,
  duplicateStoreNames,
  routeDeviationCardIds,
  geoAlerts,
  displayMetrics,
  showVehicleInfo = false,
  dropPreviewIndex,
  recentlyMovedCardIds,
  onSelectCard,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: rowId,
    data: { rowId, vehicleId: vehicle.id },
    disabled: !enabled,
  });

  // 同 row 内で同じ card.id が居ると React duplicate key 警告 + dnd-kit 不具合になるため
  // 描画直前に重複除去する。設計上は上流 (vehicleRowVM / placeLegRunInRow) で起きないはずだが、
  // FR template と実 op の組み合わせ等エッジケースの安全網として保険を入れる。
  const visibleCards = (() => {
    const seen = new Set<string>();
    return cards.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  })();
  const sortableIds = visibleCards.filter((c) => c.type === 'delivery' && c.status !== 'no-delivery').map((c) => c.id);

  // Calculate total cases for loading cards
  const rowTotalCases = visibleCards.reduce((sum, c) => {
    if (c.type === 'delivery' && c.status === 'matched' && c.todayCases !== null) return sum + c.todayCases;
    return sum;
  }, 0);

  let deliveryOrder = 0;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        height: VEHICLE_CARD_HEIGHT,
        minWidth: showVehicleInfo ? 0 : 'max-content',
        flex: showVehicleInfo ? 1 : 'none',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        transition: 'background-color 150ms',
        bgcolor: isOver ? 'hsl(var(--accent) / 0.35)' : 'hsl(var(--background) / 0.55)',
        opacity: enabled ? 1 : 0.6,
      }}
    >
      <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
        {visibleCards.map((card, idx) => {
          const orderIndex = card.type === 'delivery' ? ++deliveryOrder : 0;
          const isDelivery = card.type === 'delivery';
          return (
            <Fragment key={card.id}>
              {dropPreviewIndex === idx && <DropIndicator />}
              <DispatchCardView
                card={card}
                selected={selectedCardIds?.has(card.id) ?? false}
                showDuplicate={isDelivery && card.type === 'delivery' ? duplicateStoreNames?.has(card.storeName) ?? false : false}
                routeDeviation={routeDeviationCardIds?.has(card.id) ?? false}
                geoAlert={geoAlerts?.get(card.id)}
                totalCases={card.type === 'loading' ? rowTotalCases : undefined}
                displayMetrics={displayMetrics}
                recentlyMoved={recentlyMovedCardIds?.has(card.id) ?? false}
                onSelect={(event) => onSelectCard?.(card, vehicle, orderIndex, event)}
              />
            </Fragment>
          );
        })}
        {dropPreviewIndex === visibleCards.length && <DropIndicator />}
      </SortableContext>
    </Box>
  );
};

/** ドロップ予定位置を示す青い縦バー (アニメーション付き) */
const DropIndicator: FC = () => (
  <Box
    sx={{
      width: 4,
      height: 56,
      flexShrink: 0,
      borderRadius: 1,
      bgcolor: 'hsl(var(--primary))',
      boxShadow: '0 0 8px hsl(var(--primary) / 0.6)',
      animation: 'dropIndicatorPulse 1s ease-in-out infinite',
      '@keyframes dropIndicatorPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
      },
    }}
  />
);
